import './index.css'

import clsx from 'clsx'
import * as React from 'react'

import type { StyleObj } from 'components/utils/modalStyles'
import { DialogFooterSlotContext } from 'components/modals/dialog-footer/slot-context'
import { MetadataFormSkeleton, MetadataTabsSkeleton } from './skeleton'

type Props = {
  /** Max modal width in px; maps to the existing `modal-width--N` classes. */
  width: 640 | 850
  /** Header row content (laid out with space-between). */
  header: React.ReactNode
  /** Dims inputs / shows a progress cursor while the track reloads. */
  refreshing?: boolean
  /**
   * Render a skeleton body instead of `children`, for when the set of tabs
   * isn't known yet (the bulk editor's initial load). Once the tabs can be
   * rendered, prefer passing `isLoading` to them so the real tab strip stays
   * visible and only the tab body is a placeholder.
   */
  loading?: boolean
  /** The MetadataTabs (or equivalent tabbed body). */
  children: React.ReactNode
}

/**
 * Shared three-part chrome for the track metadata modals (single-track edit
 * and bulk edit). Renders a fixed-height flex column: a pinned header, the
 * scrolling tab body, and a footer slot pinned below it. The active tab's
 * `DialogFooter` portals into the slot via {@link DialogFooterSlotContext},
 * so the scrollbar stays within the tab content rather than running the full
 * height of the modal.
 *
 * The surrounding `DiscoModal`/`DiscoAsyncModal` is owned by each caller; pass
 * {@link metadataModalContentStyle} as its content style so both modals share
 * the same sizing, centering, and overflow behaviour.
 */
export function MetadataModalShell({
  width,
  header,
  refreshing,
  loading,
  children,
}: Props): React.ReactElement<'div'> {
  // Held in state (not a ref) so the tabs re-render once the slot exists and
  // their DialogFooter can portal into it.
  const [footerSlot, setFooterSlot] = React.useState<HTMLElement | null>(null)

  return (
    <DialogFooterSlotContext.Provider value={footerSlot}>
      <div
        className={clsx('MetadataModalShell', `modal-width--${width}`, {
          'MetadataModalShell--refreshing': refreshing,
        })}
      >
        <div className="MetadataModalShell__header">{header}</div>
        {loading ? (
          <>
            <MetadataTabsSkeleton />
            <div className="MetadataModalShell__loadingBody">
              <MetadataFormSkeleton />
            </div>
          </>
        ) : (
          children
        )}
        {/* The active tab's DialogFooter portals in here, below the scroll
            area, so it stays pinned while the tab content scrolls. */}
        <div className="MetadataModalShell__footerSlot" ref={setFooterSlot} />
      </div>
    </DialogFooterSlotContext.Provider>
  )
}

/**
 * Content-style overrides for the modal hosting {@link MetadataModalShell}.
 * Spreads over a base style object (e.g. `containedModalStyles.content`).
 */
export function metadataModalContentStyle(base: StyleObj): StyleObj {
  return {
    ...base,
    // The shell is a flex column; make sure the hosting content is one too,
    // regardless of which base style object it started from (containedModal
    // is already a flex column, default modal is not).
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 'var(--radius-4)',
    // Horizontal padding only; the chrome sections bleed to the edges and
    // re-inset their own content, and the tab body is the scroll container.
    padding: '0 24px',
    // Center on both axes; `margin: auto` in the flex overlay keeps it
    // centered while still allowing the overlay to scroll on short viewports.
    margin: 'auto',
    // Fixed height so every tab renders at the same size and the modal
    // doesn't resize when switching tabs.
    height: 'min(90vh, 820px)',
    maxHeight: 'calc(100vh - 32px)',
    // The tab body scrolls, not the modal itself.
    overflow: 'hidden',
  }
}
